import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import YhInput from "../../../../ui/YhInput";
import YhButton from "../../../../ui/YhButton";
import VisibilityIcon from "../../../../assets/icons/VisibilityIcon.svg";
import VisibilityOffIcon from "../../../../assets/icons/VisibilityOff.svg";
import { changePassword } from "./SecurityService";
import customErrorHandler from "../../../../utils/customErrorHandler";
import { useAppSelector } from "../../../../utils/hooks";
import { selectUserId } from "../../../../redux/authSlice";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import urls from "../../../../global/constants/UrlConstants";

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseSecurity?: Function;
}

const Security = (props: CustomProps = {}) => {
  const history = useHistory();
  const isEmbedded = props.isDialogOpen !== undefined;
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState<any>({
    old_password: true,
    password: true,
    confirm_password: true,
  });
  const [updatePassword, setUpdatePassword] = useState({
    old_password: { value: "", error: "" },
    password: { value: "", error: "" },
    confirm_password: { value: "", error: "" },
  });

  const userIdFromRedux = useAppSelector(selectUserId);

  const handleClose = () => {
    if (props.handleCloseSecurity) {
      props.handleCloseSecurity();
      return;
    }
    history.push(urls.accountSettingsViewPth);
  };

  const onChangeHandle = (event: any) => {
    const { name, value } = event.target;
    setUpdatePassword((prevState: any) => ({
      ...prevState,
      [name]: { value, error: "" },
    }));
  };

  const handleValidation = () => {
    const updatedUserInfo = JSON.parse(JSON.stringify(updatePassword));
    if (updatedUserInfo.old_password.value === "") {
      updatedUserInfo.old_password.error = "enter a valid password";
    } else if (updatedUserInfo.old_password.value.length < 8) {
      updatedUserInfo.old_password.error =
        "length should be atleat of 8 character";
    }
    if (updatedUserInfo.password.value === "") {
      updatedUserInfo.password.error = "enter a valid password";
    } else if (updatedUserInfo.password.value?.length < 8) {
      updatedUserInfo.password.error = "Password should be atlest 8 character";
    } else if (
      updatedUserInfo.password.value !==
      updatedUserInfo.confirm_password.value
    ) {
      updatedUserInfo.confirm_password.error = "password is not matched";
    }

    setUpdatePassword(updatedUserInfo);
    if (
      updatedUserInfo.old_password.error ||
      updatedUserInfo.password.error ||
      updatedUserInfo.confirm_password.error
    ) {
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!handleValidation()) return;
    try {
      setSaving(true);
      const response: any = await changePassword(
        updatePassword,
        userIdFromRedux
      );
      customErrorHandler(response);
      openSuccessNotification(response.message);
      handleClose();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClickShowPassword = (property: any) => {
    setShowPassword((prevState: any) => ({
      ...prevState,
      [property]: !prevState[property],
    }));
  };

  const EyeToggle = ({ field }: { field: string }) => (
    <button
      type="button"
      className="flex items-center"
      onClick={() => handleClickShowPassword(field)}
      tabIndex={-1}
    >
      <img
        src={showPassword[field] ? VisibilityOffIcon : VisibilityIcon}
        alt=""
        className="h-4 w-4"
      />
    </button>
  );

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,720px)] w-full max-w-3xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B]">
            Security
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage password and advanced security settings.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden pb-20">
        <section className="h-full min-h-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          <div className="mb-6">
            <h2 className="text-base font-semibold text-[#111827]">Password</h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              We recommend you change the password every 3 months to keep your
              account secure.
            </p>
          </div>

          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <YhInput
                label="Old Password"
                placeholder="Old Password"
                type={showPassword.old_password ? "password" : "text"}
                name="old_password"
                value={updatePassword.old_password.value}
                error={updatePassword.old_password.error}
                onChange={onChangeHandle}
                endAdornment={<EyeToggle field="old_password" />}
              />
            </div>
            <YhInput
              label="New Password"
              placeholder="New Password"
              type={showPassword.password ? "password" : "text"}
              name="password"
              value={updatePassword.password.value}
              error={updatePassword.password.error}
              onChange={onChangeHandle}
              endAdornment={<EyeToggle field="password" />}
            />
            <YhInput
              label="Confirm Password"
              placeholder="Confirm Password"
              type={showPassword.confirm_password ? "password" : "text"}
              name="confirm_password"
              value={updatePassword.confirm_password.value}
              error={updatePassword.confirm_password.error}
              onChange={onChangeHandle}
              endAdornment={<EyeToggle field="confirm_password" />}
            />
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={submit}
          >
            Save
          </YhButton>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-3xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default Security;
